import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { setEntities, withEntities } from '@ngrx/signals/entities';

import { Specification } from '@celum/formats/domain';

import { ASSIGNMENTS_SERVICE } from '../../services/assignments.service';
import { NotificationService } from '../../services/notification.service';

type UnassignedSpecificationsState = {
  loading: boolean;
  pagination: {
    totalCount: number;
    page: number;
    size: number;
    hasNext: boolean;
  };
  selectedSpecificationIds: number[] | null;
};

const initialState: UnassignedSpecificationsState = {
  loading: true,
  pagination: {
    totalCount: 0,
    page: 0,
    size: 20,
    hasNext: false
  },
  selectedSpecificationIds: null
};

export const UnassignedSpecificationsStore = signalStore(
  withEntities<Specification>(),
  withState(initialState),
  withComputed(({ selectedSpecificationIds, entityMap }) => ({
    selectedSpecifications: computed(() => (selectedSpecificationIds() ? selectedSpecificationIds().map(id => entityMap()[id]) : []))
  })),
  withMethods((store, assignmentsService = inject(ASSIGNMENTS_SERVICE), notificationService = inject(NotificationService)) => {
    function loadUnassignedSpecificationsBatch(): void {
      patchState(store, { loading: true });

      const { page, size } = store.pagination();
      assignmentsService.getTenantSpecifications(page, size, true).subscribe({
        next: ({ content, hasNext, totalElements }) => {
          patchState(store, setEntities(content));
          patchState(store, {
            loading: false,
            pagination: {
              ...store.pagination(),
              totalCount: totalElements,
              hasNext
            }
          });
        },
        error: err => {
          console.error('UnassignedSpecificationsStore: Error loading unassigned specifications', err);
          notificationService.error(err?.error?.errorMessage);
          patchState(store, { loading: false });
        }
      });
    }

    function loadNextUnassignedSpecificationsBatch(): void {
      const { page } = store.pagination();

      patchState(store, { pagination: { ...store.pagination(), page: page + 1 } });
      loadUnassignedSpecificationsBatch();
    }

    function toggleSpecificationSelection(id: number): void {
      const currentSelection = store.selectedSpecificationIds();
      const newSelection = currentSelection ? [...currentSelection] : [];

      if (newSelection.includes(id)) {
        // Remove from selection
        const index = newSelection.indexOf(id);
        if (index > -1) {
          newSelection.splice(index, 1);
        }
      } else {
        // Add to selection
        newSelection.push(id);
      }

      patchState(store, { selectedSpecificationIds: newSelection.length > 0 ? newSelection : null });
    }

    return {
      loadUnassignedSpecificationsBatch,
      loadNextUnassignedSpecificationsBatch,
      toggleSpecificationSelection
    };
  })
);
